import type { OfflineStorage } from '../ports';

/**
 * §28 — the browser's durable {@link OfflineStorage}, and the ONE file in this
 * package that knows a browser exists.
 *
 * **Why not `localStorage`.** It is *synchronous*, so every queue read blocks
 * the thread that is meant to be drawing the till. It is capped at roughly 5MB
 * per origin, which a busy shift's queued orders do not fit; the write that
 * overflows throws `QuotaExceededError` in the middle of `enqueue()`, which is
 * the order the person just took. And its writes are not transactional, so a
 * tab killed mid-write can leave a truncated string that `JSON.parse` rejects,
 * turning the whole queue into a parse error. IndexedDB is asynchronous,
 * quota-backed in the hundreds of megabytes, and commits atomically. Do not
 * "simplify" this file back to `localStorage`.
 *
 * **Isolation.** Each namespace gets its own IndexedDB *database*, so the
 * dashboard and customer-web served from one origin cannot collide even though
 * both default to the key `venue.outbox`. Same origin is one trust domain and
 * any script there can open any database by name: this prevents *accidents*,
 * not an attacker.
 *
 * **Concurrency.** See {@link ConcurrentWriteError} — two tabs of the same app.
 *
 * **Import shape.** Everything DOM-ish here is a locally declared structural
 * type (this package compiles with `lib: ["ES2022"]` — there is no DOM lib to
 * lean on) and every global is read inside {@link createIndexedDbStorage}, not
 * at module scope. Importing this file, or the barrel that re-exports it, runs
 * no browser code at all, which is why a React Native host can keep importing
 * `@/lib/offline` without dragging `indexedDB` in.
 */

// --------------------------------------------------------------- host types
//
// Minimal structural shapes for the slice of IndexedDB used below: no `dom` lib
// and no `@types/*` needed, and `fake-indexeddb` satisfies them in the tests.

interface IdbRequestLike<T> {
  result: T;
  error: unknown;
  onsuccess: ((event: unknown) => void) | null;
  onerror: ((event: unknown) => void) | null;
}

interface IdbOpenRequestLike extends IdbRequestLike<IdbDatabaseLike> {
  onupgradeneeded: ((event: unknown) => void) | null;
  onblocked: ((event: unknown) => void) | null;
}

interface IdbObjectStoreLike {
  get(key: string): IdbRequestLike<unknown>;
  put(value: unknown): IdbRequestLike<unknown>;
  delete(key: string): IdbRequestLike<unknown>;
}

interface IdbTransactionLike {
  objectStore(name: string): IdbObjectStoreLike;
  abort(): void;
  error: unknown;
  oncomplete: ((event: unknown) => void) | null;
  onerror: ((event: unknown) => void) | null;
  onabort: ((event: unknown) => void) | null;
}

interface IdbDatabaseLike {
  transaction(
    storeNames: string,
    mode: 'readonly' | 'readwrite',
    options?: { durability?: 'strict' | 'relaxed' | 'default' },
  ): IdbTransactionLike;
  createObjectStore(name: string, options?: { keyPath?: string }): IdbObjectStoreLike;
  objectStoreNames: { contains(name: string): boolean };
  close(): void;
  onversionchange: ((event: unknown) => void) | null;
}

export interface IndexedDbFactoryLike {
  open(name: string, version?: number): IdbOpenRequestLike;
}

/**
 * `navigator.locks`, narrowed. Optional: without it the adapter loses its
 * cross-tab serialisation and leans entirely on the version check.
 */
export interface WebLockManagerLike {
  request<T>(name: string, callback: () => Promise<T>): Promise<T>;
}

/**
 * IndexedDB is not usable here, thrown from {@link createIndexedDbStorage} and
 * never from a write: private browsing, a disabled origin, a zero quota, a
 * corrupt profile. The alternative — hand back a storage object and let the
 * first queued order discover the problem — is precisely the bug this package
 * exists to prevent: the person is told their order is queued, nothing is on
 * disk, and nobody finds out until the shift is reconciled. Fail at
 * construction, loudly, so the host can refuse to run offline rather than
 * pretend.
 */
export class IndexedDbUnavailableError extends Error {
  constructor(message: string, cause?: unknown) {
    super(message, { cause });
    this.name = 'IndexedDbUnavailableError';
    // Hermes and older engines drop the options bag; keep the cause reachable.
    if (this.cause === undefined && cause !== undefined) this.cause = cause;
  }
}

/**
 * Another tab wrote this key between our read and our write, so the write was
 * refused instead of applied.
 *
 * **The two-tab problem.** The outbox serialises its own read-modify-write
 * pairs through one promise chain, but that chain is per JavaScript realm: two
 * tabs are two chains, and IndexedDB offers no lock spanning a `getItem` and
 * the `setItem` that follows it. Left alone, tab A reads `[]`, tab B enqueues
 * an order, tab A writes `[]` back, and the order is gone with no trace. So
 * every record carries a version: a write states the version it read and is
 * applied only if the stored version still matches, otherwise the transaction
 * aborts and this is thrown. Nothing is overwritten and nothing is discarded —
 * the losing side finds out.
 *
 * What each caller does with it:
 * - `enqueue()` / `submit()` / `mutate()` reject, so the person is told the
 *   action was not accepted instead of watching it vanish.
 * - the flush loop's post-delivery `removeFromQueue()` lands in its own catch,
 *   spends one attempt and leaves the entry queued; the next flush re-sends it
 *   and the replay key makes the second delivery a no-op. Duplicate delivery is
 *   already safe by construction, which is why erring in this direction is
 *   correct. Past `maxAttempts` the entry dead-letters, where a person can see
 *   it — still never silent.
 *
 * With `navigator.locks` (Chrome 69+, Firefox 96+, Safari 15.4+) each operation
 * is serialised across tabs, so reaching this needs two tabs mutating the same
 * key within the same few milliseconds: a backstop, not the normal path. A host
 * that wants zero conflicts should elect a single flusher per namespace — hold
 * a Web Lock for the app's lifetime, or a `BroadcastChannel` election — and let
 * the passive tabs enqueue only. That belongs to the host because the host owns
 * the flush trigger; a three-method key/value port cannot decide it.
 */
export class ConcurrentWriteError extends Error {
  constructor(
    readonly key: string,
    readonly expectedVersion: number,
    readonly actualVersion: number,
  ) {
    super(
      `Another tab wrote "${key}" first (expected version ${expectedVersion}, found ${actualVersion}); ` +
        'the write was refused rather than applied over it',
    );
    this.name = 'ConcurrentWriteError';
  }
}

export interface IndexedDbStorageOptions {
  /**
   * Pass the same value as the outbox's `namespace`. It picks the database, so
   * two apps on one origin stay apart.
   */
  namespace: string;
  /** Database name prefix. Defaults to `venue.offline`. */
  databasePrefix?: string;
  /** Defaults to `globalThis.indexedDB`. Injectable for tests. */
  indexedDB?: IndexedDbFactoryLike;
  /**
   * Defaults to `globalThis.navigator.locks` when present. Pass `null` to opt
   * out and rely on the version check alone.
   */
  locks?: WebLockManagerLike | null;
}

export interface IndexedDbStorage extends OfflineStorage {
  readonly databaseName: string;
  /** Release the handle. The outbox never calls this; a test or teardown does. */
  close(): void;
}

interface StoredRecord {
  key: string;
  value: string;
  /** Bumped on every write. The whole cross-tab guard rests on this. */
  version: number;
  updatedAt: number;
}

const STORE = 'kv';
const PROBE_KEY = '__venue.offline.probe';
/**
 * A namespace becomes a database name, so it must map one-to-one: no whitespace
 * and no separators that let `a.b` and `a` + `.b` land in one place.
 */
const NAMESPACE = /^[A-Za-z0-9][A-Za-z0-9._:-]{0,63}$/;

// ------------------------------------------------------------- IDB plumbing

/**
 * `durability: 'strict'` asks the browser to flush to disk before the
 * transaction reports complete. The default `relaxed` acknowledges a write the
 * OS has only buffered, which a crash or force-quit can lose — fine for a
 * cache, not for the order a guest is waiting on. Engines predating the option
 * ignore the dictionary; the `catch` covers one that refuses it outright.
 */
function begin(
  db: IdbDatabaseLike,
  mode: 'readonly' | 'readwrite',
): IdbTransactionLike {
  try {
    return db.transaction(STORE, mode, { durability: 'strict' });
  } catch {
    return db.transaction(STORE, mode);
  }
}

function failure(message: string, cause: unknown): Error {
  const error = new Error(message);
  (error as { cause?: unknown }).cause = cause;
  return error;
}

/** Resolve on `oncomplete` — not on request success — so durability is real. */
function committed<T>(
  transaction: IdbTransactionLike,
  read: () => T,
  what: string,
  onAbort?: () => Error | null,
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    transaction.oncomplete = () => resolve(read());
    transaction.onerror = () => reject(failure(`${what} failed`, transaction.error));
    transaction.onabort = () => {
      const deliberate = onAbort?.();
      reject(deliberate ?? failure(`${what} was aborted`, transaction.error));
    };
  });
}

function openDatabase(
  factory: IndexedDbFactoryLike,
  name: string,
): Promise<IdbDatabaseLike> {
  return new Promise((resolve, reject) => {
    let request: IdbOpenRequestLike;
    try {
      // Firefox in private browsing has historically thrown right here.
      request = factory.open(name, 1);
    } catch (error) {
      reject(new IndexedDbUnavailableError(`Could not open IndexedDB "${name}"`, error));
      return;
    }

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE, { keyPath: 'key' });
    };
    request.onblocked = () =>
      reject(
        new IndexedDbUnavailableError(
          `IndexedDB "${name}" is blocked by another tab holding an older version open`,
        ),
      );
    request.onerror = () =>
      reject(new IndexedDbUnavailableError(`Could not open IndexedDB "${name}"`, request.error));
    request.onsuccess = () => resolve(request.result);
  });
}

/**
 * Write, read back and clean up in one transaction.
 *
 * Opening a database is not proof that it can hold anything: Safari's private
 * mode and a zero quota both hand back a working handle whose first write
 * fails. Only "an order written here will still be here" is worth reporting.
 */
function probe(db: IdbDatabaseLike, name: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const token = `probe-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    let readBack: string | null = null;
    let transaction: IdbTransactionLike;
    try {
      transaction = begin(db, 'readwrite');
    } catch (error) {
      reject(new IndexedDbUnavailableError(`IndexedDB "${name}" refused a transaction`, error));
      return;
    }

    const store = transaction.objectStore(STORE);
    const write = store.put({ key: PROBE_KEY, value: token, version: 0, updatedAt: Date.now() });
    write.onsuccess = () => {
      const back = store.get(PROBE_KEY);
      back.onsuccess = () => {
        readBack = (back.result as StoredRecord | undefined)?.value ?? null;
        store.delete(PROBE_KEY);
      };
    };

    transaction.oncomplete = () => {
      if (readBack === token) resolve();
      else
        reject(
          new IndexedDbUnavailableError(
            `IndexedDB "${name}" accepted a write but did not return it — refusing to queue work here`,
          ),
        );
    };
    const fail = () =>
      reject(
        new IndexedDbUnavailableError(
          `IndexedDB "${name}" cannot be written to (private browsing, or no quota)`,
          transaction.error,
        ),
      );
    transaction.onerror = fail;
    transaction.onabort = fail;
  });
}

function readRecord(db: IdbDatabaseLike, key: string): Promise<StoredRecord | undefined> {
  const transaction = begin(db, 'readonly');
  let record: StoredRecord | undefined;
  const request = transaction.objectStore(STORE).get(key);
  request.onsuccess = () => {
    record = request.result as StoredRecord | undefined;
  };
  return committed(transaction, () => record, `Reading "${key}" from IndexedDB`);
}

/**
 * Compare-and-set. `expected` is the version this instance last observed;
 * `undefined` means it never read the key, which only happens if a caller
 * writes without reading — the outbox always reads first.
 */
function writeRecord(
  db: IdbDatabaseLike,
  key: string,
  value: string,
  expected: number | undefined,
): Promise<number> {
  const transaction = begin(db, 'readwrite');
  const store = transaction.objectStore(STORE);
  let next = 0;
  let conflict: ConcurrentWriteError | null = null;

  const current = store.get(key);
  // The put is issued from inside the get's success handler, so the pair is one
  // transaction with no chance of the browser auto-committing between them.
  current.onsuccess = () => {
    const actual = (current.result as StoredRecord | undefined)?.version ?? 0;
    if (expected !== undefined && actual !== expected) {
      conflict = new ConcurrentWriteError(key, expected, actual);
      transaction.abort();
      return;
    }
    next = actual + 1;
    store.put({ key, value, version: next, updatedAt: Date.now() });
  };

  return committed(
    transaction,
    () => next,
    `Writing "${key}" to IndexedDB`,
    () => conflict,
  );
}

function deleteRecord(db: IdbDatabaseLike, key: string): Promise<void> {
  const transaction = begin(db, 'readwrite');
  transaction.objectStore(STORE).delete(key);
  return committed(transaction, () => undefined, `Removing "${key}" from IndexedDB`);
}

/**
 * Build an IndexedDB-backed {@link OfflineStorage} for one namespace.
 *
 * Async because whether this origin can durably hold a queue is only knowable
 * after opening the database and writing to it, and the caller must learn the
 * answer before it accepts a single order. It rejects with
 * {@link IndexedDbUnavailableError} rather than degrading to something lossy —
 * a host that wants an in-memory fallback must choose it explicitly, and tell
 * the person their work will not survive a reload.
 */
export async function createIndexedDbStorage(
  options: IndexedDbStorageOptions,
): Promise<IndexedDbStorage> {
  const { namespace } = options;
  if (!NAMESPACE.test(namespace ?? '')) {
    throw new IndexedDbUnavailableError(
      `Invalid namespace ${JSON.stringify(namespace)}: it names the database that keeps two apps ` +
        'on one origin apart, so it must match /^[A-Za-z0-9][A-Za-z0-9._:-]{0,63}$/',
    );
  }

  const host = globalThis as {
    indexedDB?: IndexedDbFactoryLike;
    navigator?: { locks?: WebLockManagerLike };
  };
  const factory = options.indexedDB ?? host.indexedDB;
  if (!factory || typeof factory.open !== 'function') {
    throw new IndexedDbUnavailableError(
      'IndexedDB is not available in this environment (private browsing, or storage disabled). ' +
        'Refusing to hand back a store that would accept queued work and lose it.',
    );
  }

  const locks =
    options.locks === null ? null : (options.locks ?? host.navigator?.locks ?? null);

  const databaseName = `${options.databasePrefix ?? 'venue.offline'}.${namespace}`;
  const db = await openDatabase(factory, databaseName);
  try {
    await probe(db, databaseName);
  } catch (error) {
    db.close();
    throw error;
  }

  let closed = false;
  // Another tab deleting or upgrading this database must not be blocked by us.
  db.onversionchange = () => {
    closed = true;
    db.close();
  };

  /** Per-key versions this instance has seen; the input to every CAS. */
  const observed = new Map<string, number>();

  /**
   * One operation at a time per key: across tabs through Web Locks, and within
   * this tab through a promise chain when the browser has none. It narrows the
   * window the version check has to catch; it cannot close it, because the
   * window spans two separate port calls.
   */
  let chain: Promise<unknown> = Promise.resolve();
  function serialised<T>(key: string, work: () => Promise<T>): Promise<T> {
    const guarded = async () => {
      if (closed) {
        throw new IndexedDbUnavailableError(
          `IndexedDB "${databaseName}" was closed by another tab's version change`,
        );
      }
      return work();
    };
    if (locks) return locks.request(`${databaseName}#${key}`, guarded);
    const run = chain.then(guarded, guarded);
    chain = run.then(
      () => undefined,
      () => undefined,
    );
    return run;
  }

  return {
    databaseName,

    /**
     * `null` means "never written", exactly as the port requires — and *only*
     * that. A real read failure throws instead, because the outbox reads before
     * every write: an unreadable queue reported as empty would be written back
     * as empty, deleting a shift's orders on the way past.
     */
    async getItem(key) {
      return serialised(key, async () => {
        const record = await readRecord(db, key);
        observed.set(key, record?.version ?? 0);
        return record ? record.value : null;
      });
    },

    async setItem(key, value) {
      await serialised(key, async () => {
        const version = await writeRecord(db, key, value, observed.get(key));
        observed.set(key, version);
      });
    },

    /**
     * An unconditional delete, on purpose. The outbox only removes a key from
     * `clear()` and `clearDead()`, explicit human instructions to throw the
     * queue away — a version check there would let a background flush veto a
     * person's decision. A later write from this instance still expects version
     * 0 and so still catches a tab that wrote in the meantime.
     */
    async removeItem(key) {
      await serialised(key, async () => {
        await deleteRecord(db, key);
        observed.set(key, 0);
      });
    },

    close() {
      closed = true;
      db.close();
    },
  };
}
